import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaTasks,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaTrash,
  FaList,
  FaClock,
  FaCheckCircle,
  FaUser,
  FaCalendarAlt,
  FaClipboardList,
} from "react-icons/fa";
import { DeleteModal } from "../Fields/FieldDetails";
import "./Tasks.css";

const Tasks = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { token, userData } = useContext(UserContext);

  // State Management
  const [selectedTab, setSelectedTab] = useState("today");
  const [viewMode, setViewMode] = useState("tasks"); // tasks or lists
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [tasksCount, setTasksCount] = useState(0);

  // Tasks data by period
  const [todayTasks, setTodayTasks] = useState([]);
  const [plannedTasks, setPlannedTasks] = useState([]);
  const [thisWeekCompleted, setThisWeekCompleted] = useState([]);
  const [lastWeekCompleted, setLastWeekCompleted] = useState([]);
  const [olderCompleted, setOlderCompleted] = useState([]);

  // Task lists
  const [taskLists, setTaskLists] = useState([]);

  // UI state
  const [expandedSections, setExpandedSections] = useState({
    thisWeek: true,
    lastWeek: false,
    older: false,
  });

  // Track expanded task lists (for showing/hiding tasks under each list)
  const [expandedTaskLists, setExpandedTaskLists] = useState({});

  // Delete modal state
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTaskList, setSelectedTaskList] = useState(null);

  // Date range calculations - matching Android implementation EXACTLY
  const getDateRanges = () => {
    const now = new Date();

    // TODAY: Start and end of current day
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date(now);
    todayEnd.setHours(23, 59, 59, 999);

    // FUTURE: Tomorrow onwards
    const futureStart = new Date(now);
    futureStart.setDate(futureStart.getDate() + 1);
    futureStart.setHours(0, 0, 0, 0);

    // OLDER: Before today
    const olderEnd = new Date(now);
    olderEnd.setDate(olderEnd.getDate() - 1);
    olderEnd.setHours(23, 59, 59, 999);

    // Android's CURRENT_WEEK: Last 7 days excluding today (yesterday to 7 days ago)
    const currentWeekEnd = new Date(now);
    currentWeekEnd.setDate(currentWeekEnd.getDate() - 1); // Yesterday
    currentWeekEnd.setHours(23, 59, 59, 999);

    const currentWeekStart = new Date(now);
    currentWeekStart.setDate(currentWeekStart.getDate() - 7); // 7 days ago
    currentWeekStart.setHours(0, 0, 0, 0);

    // Android's PREVIOUS_WEEK: The 7 days before current week
    const previousWeekEnd = new Date(currentWeekStart);
    previousWeekEnd.setMilliseconds(previousWeekEnd.getMilliseconds() - 1);

    const previousWeekStart = new Date(currentWeekStart);
    previousWeekStart.setDate(previousWeekStart.getDate() - 7);
    previousWeekStart.setHours(0, 0, 0, 0);

    return {
      today: { start: todayStart.getTime(), end: todayEnd.getTime() },
      future: { start: futureStart.getTime() },
      older: { end: olderEnd.getTime() },
      currentWeek: {
        start: currentWeekStart.getTime(),
        end: currentWeekEnd.getTime(),
      },
      previousWeek: {
        start: previousWeekStart.getTime(),
        end: previousWeekEnd.getTime(),
      },
    };
  };

  // Fetch tasks based on period with optional date ranges
  const fetchTasks = useCallback(
    async (period) => {
      if (!userData?.Farm?.id || !token) return;

      setLoading(true);
      const dates = getDateRanges();

      try {
        const params = {
          period,
          employeeId: userData.Employee?.id || userData.id,
        };

        // Add date ranges based on period - NEW LOGIC from Dashboard
        if (period === "today") {
          params.fromDate = dates.today.start;
          params.toDate = dates.today.end;
        } else if (period === "future") {
          params.fromDate = dates.future.start;
        } else if (period === "older") {
          params.toDate = dates.older.end;
        }

        const response = await axios.get(
          `/api/task/findByFarmId/${userData.Farm.id}`,
          {
            params,
            headers: { Authorization: token },
          }
        );

        // Extract tasks from the nested structure
        // API returns items like: { task: {...}, field: {...}, employee: {...}, taskCategory: {...} }
        const rawItems = response.data.items || [];
        const tasks = rawItems
          .map((item) => ({
            ...item.task, // Main task data
            field: item.field, // Add field info
            assignedEmployee: item.employee, // Add employee info
            taskCategory: item.taskCategory, // Add category info
          }))
          .filter((task) => task && task.id); // Filter out any invalid tasks

        // Process tasks based on period
        if (period === "today") {
          setTodayTasks(tasks);
          setTasksCount(tasks.length);
        } else if (period === "future") {
          setPlannedTasks(tasks);
          setTasksCount(tasks.length);
        } else if (period === "older") {
          // Split older tasks into weekly groups - matching Android logic EXACTLY
          const thisWeek = [];
          const previousWeek = [];
          const olderTasks = [];

          tasks.forEach((task) => {
            // Use fromDate as backend returns (not dateFrom)
            const taskDate = task.fromDate || task.toDate;

            if (!taskDate) {
              // Skip tasks without dates
              return;
            }

            // Match Android's exact conditions
            // Android uses: task.getDateFrom() > range.getFrom() && task.getDateFrom() < range.getTo()
            if (
              taskDate >= dates.currentWeek.start &&
              taskDate <= dates.currentWeek.end
            ) {
              // This week: From 7 days ago (start of day) to yesterday (end of day)
              thisWeek.push(task);
            } else if (
              taskDate >= dates.previousWeek.start &&
              taskDate <= dates.previousWeek.end
            ) {
              // Previous week: From 14 days ago to just before current week
              previousWeek.push(task);
            } else if (taskDate < dates.previousWeek.start) {
              // Older: Everything before previous week start (more than 14 days ago)
              olderTasks.push(task);
            } else {
              // Edge case: Tasks that are exactly today or in the future
              // Put it in the most appropriate category based on date
              if (taskDate > dates.currentWeek.end) {
                // Future or today task - add to this week
                thisWeek.push(task);
              } else {
                // Fallback to older
                olderTasks.push(task);
              }
            }
          });

          // Sort by date descending (newest first) - matching Android's sorted(list, true)
          thisWeek.sort(
            (a, b) => (b.fromDate || b.toDate) - (a.fromDate || a.toDate)
          );
          previousWeek.sort(
            (a, b) => (b.fromDate || b.toDate) - (a.fromDate || a.toDate)
          );
          olderTasks.sort(
            (a, b) => (b.fromDate || b.toDate) - (a.fromDate || a.toDate)
          );

          setThisWeekCompleted(thisWeek);
          setLastWeekCompleted(previousWeek);
          setOlderCompleted(olderTasks);
          setTasksCount(tasks.length);
        }
      } catch (error) {
        console.error("Failed to fetch tasks:", error);
        toast.error(t("Failed to load tasks"));
      } finally {
        setLoading(false);
      }
    },
    [userData, token, t]
  );

  // Fetch task lists with their tasks
  const fetchTaskLists = useCallback(async () => {
    if (!userData?.Farm?.id || !token) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `/api/taskList/findByFarmId/${userData.Farm.id}`,
        { headers: { Authorization: token } }
      );
      // Store full task list with tasks data
      const listsWithTasks = response.data.items || [];
      setTaskLists(listsWithTasks);
      setTasksCount(listsWithTasks.length);
    } catch (error) {
      console.error("Failed to fetch task lists:", error);
      toast.error(t("Failed to load task lists"));
    } finally {
      setLoading(false);
    }
  }, [userData, token, t]);

  // Filter tasks by status - matching Android implementation exactly
  const filterTasksByStatus = (tasks) => {
    if (!tasks || tasks.length === 0) return [];

    // Filter index 0 = "All" - return all tasks
    if (filterStatus === "all") return tasks;

    return tasks.filter((task) => {
      // Backend returns state as strings: "notStarted", "inProgress", "finished"
      const taskState = (
        task.state ||
        task.status ||
        "notStarted"
      ).toLowerCase();

      // Map filter selection to backend state strings
      switch (filterStatus) {
        case "todo":
          return taskState === "notstarted";
        case "inprogress":
          return taskState === "inprogress";
        case "done":
          return taskState === "finished";
        default:
          return true;
      }
    });
  };

  // Delete task list handlers
  const handleDeleteTaskList = (taskList) => {
    setSelectedTaskList(taskList);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTaskList || !token) return;

    try {
      await axios.delete(`/api/taskList/${selectedTaskList.id}`, {
        headers: { Authorization: token },
      });
      toast.success(t("Task list deleted successfully"));
      fetchTaskLists();
    } catch (error) {
      console.error("Failed to delete task list:", error);
      toast.error(t("Failed to delete task list"));
    } finally {
      setDeleteModalOpen(false);
      setSelectedTaskList(null);
    }
  };

  const handleCloseModal = () => {
    setDeleteModalOpen(false);
    setSelectedTaskList(null);
  };

  // Load initial data
  useEffect(() => {
    if (viewMode === "tasks") {
      fetchTasks(selectedTab);
    } else {
      fetchTaskLists();
    }
  }, [selectedTab, viewMode, fetchTasks, fetchTaskLists]);

  // Refresh when navigating back from task details
  useEffect(() => {
    if (routerLocation.state?.refresh) {
      if (viewMode === "tasks") {
        fetchTasks(selectedTab);
      } else {
        fetchTaskLists();
      }
      // Clear the refresh flag
      navigate(routerLocation.pathname, { replace: true, state: {} });
    }
  }, [
    routerLocation,
    viewMode,
    selectedTab,
    fetchTasks,
    fetchTaskLists,
    navigate,
  ]);

  // Internal Components
  const TaskItem = ({ task }) => {
    // Get the task state - backend returns strings: "notStarted", "inProgress", "finished"
    const taskState = task.state || task.status || "notStarted";

    const getStatusClass = (state) => {
      // Map backend state strings to CSS classes
      const stateStr = String(state).toLowerCase();

      switch (stateStr) {
        case "finished":
          return "status-done";
        case "notstarted":
          return "status-todo";
        case "inprogress":
          return "status-inprogress";
        default:
          return "status-todo";
      }
    };

    const getStatusLabel = (state) => {
      // Map backend state strings to display labels
      const stateStr = String(state).toLowerCase();

      switch (stateStr) {
        case "finished":
          return t("Done");
        case "notstarted":
          return t("To Do");
        case "inprogress":
          return t("In Progress");
        default:
          return t("To Do");
      }
    };

    return (
      <div
        className="task-item-card"
        onClick={() =>
          navigate(`/fields/${userData?.Farm?.id}/tasks/${task.id}`)
        }
      >
        <div className="task-item-header">
          <h4 className="task-title">{task.name}</h4>
          <span className={`task-status-badge ${getStatusClass(taskState)}`}>
            {getStatusLabel(taskState)}
          </span>
        </div>

        {task.textDesc && <p className="task-description">{task.textDesc}</p>}

        <div className="task-meta">
          {task.field && (
            <div className="task-meta-item">
              <FaClipboardList className="meta-icon" />
              <span>{task.field.name}</span>
            </div>
          )}

          {task.assignedEmployee && (
            <div className="task-meta-item">
              <FaUser className="meta-icon" />
              <span>
                {task.assignedEmployee.username || task.assignedEmployee.name}
              </span>
            </div>
          )}

          {task.taskCategory && (
            <div className="task-meta-item">
              <FaTasks className="meta-icon" />
              <span>
                {i18n.language === "ar"
                  ? task.taskCategory.nameAr
                  : task.taskCategory.nameEn}
              </span>
            </div>
          )}

          <div className="task-meta-item">
            <FaCalendarAlt className="meta-icon" />
            <span>
              {new Date(
                task.fromDate || task.dateFrom || task.dateTo
              ).toLocaleDateString(i18n.language === "ar" ? "ar-EG" : "en-GB")}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const TaskListItem = ({ taskListWithTasks }) => {
    const taskListData = taskListWithTasks.taskList;
    const tasks = taskListWithTasks.tasksWithAllDetails || [];
    const isExpanded = expandedTaskLists[taskListData.id];

    const toggleExpand = () => {
      setExpandedTaskLists((prev) => ({
        ...prev,
        [taskListData.id]: !prev[taskListData.id],
      }));
    };

    return (
      <div className="task-list-card">
        <div className="task-list-header" onClick={toggleExpand}>
          <div className="task-list-header-left">
            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            <h4>{taskListData.name}</h4>
          </div>
          <div className="task-list-header-right">
            <span className="task-count-badge">
              {tasks.length} {t("tasks")}
            </span>
            <button
              className="icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/fields/${userData?.Farm?.id}/tasklists/${taskListData.id}/edit`,
                  {
                    state: { taskList: taskListData },
                  }
                );
              }}
              title={t("Edit")}
            >
              <FaEdit />
            </button>
            <button
              className="icon-btn delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteTaskList(taskListData);
              }}
              title={t("Delete")}
            >
              <FaTrash />
            </button>
          </div>
        </div>

        <div className="task-list-info">
          {taskListData.fromDate && taskListData.toDate && (
            <div className="task-list-dates">
              <FaCalendarAlt className="meta-icon" />
              <span>
                {new Date(taskListData.fromDate).toLocaleDateString(
                  i18n.language === "ar" ? "ar-EG" : "en-GB"
                )}{" "}
                -{" "}
                {new Date(taskListData.toDate).toLocaleDateString(
                  i18n.language === "ar" ? "ar-EG" : "en-GB"
                )}
              </span>
            </div>
          )}
        </div>

        {isExpanded && tasks.length > 0 && (
          <div className="task-list-tasks">
            {tasks.map((taskWithDetails) => {
              // Extract data from TaskWithAllDetails - backend returns objects or null
              const task = taskWithDetails.task || {};
              const field = taskWithDetails.field; // Can be object or null
              const taskCategory = taskWithDetails.taskCategory; // Can be object or null
              const employee = taskWithDetails.employee; // Can be object or null

              // Check if assignments array exists (future backend update)
              // Otherwise fall back to single employee field
              const assignments = taskWithDetails.assignments || [];

              // Determine employee display
              let employeeDisplay = null;
              if (assignments && assignments.length > 0) {
                // If assignments array exists (when backend is updated)
                const firstAssignment = assignments[0];
                employeeDisplay = firstAssignment.employeeName;
                if (assignments.length > 1) {
                  employeeDisplay += ` +${assignments.length - 1}`;
                }
              } else if (employee) {
                // Current implementation: single employee from TaskWithAllDetails
                employeeDisplay = employee.username || employee.name;
              }

              return (
                <div
                  key={task.id}
                  className="task-list-task-item"
                  onClick={() =>
                    navigate(`/fields/${userData?.Farm?.id}/tasks/${task.id}`)
                  }
                >
                  <div className="task-list-task-content">
                    {/* Task name and status badge */}
                    <div className="task-item-header-row">
                      <h5 className="task-list-task-title">{task.name}</h5>
                      <span
                        className={`task-status-badge ${
                          (task.state || "").toLowerCase() === "finished"
                            ? "status-done"
                            : (task.state || "").toLowerCase() === "inprogress"
                            ? "status-progress"
                            : "status-todo"
                        }`}
                      >
                        {(task.state || "").toLowerCase() === "finished"
                          ? t("Done")
                          : (task.state || "").toLowerCase() === "inprogress"
                          ? t("In Progress")
                          : t("To Do")}
                      </span>
                    </div>

                    {/* Description */}
                    {task.textDesc && (
                      <p className="task-list-task-desc">{task.textDesc}</p>
                    )}

                    {/* Field name and task category */}
                    {(field || taskCategory) && (
                      <div className="task-field-category">
                        {field && (
                          <span className="field-name">{field.name}</span>
                        )}
                        {taskCategory && (
                          <span className="task-category">
                            (
                            {i18n.language === "ar"
                              ? taskCategory.nameAr
                              : taskCategory.nameEn}
                            )
                          </span>
                        )}
                      </div>
                    )}

                    {/* Bottom row: Employee and dates */}
                    <div className="task-bottom-row">
                      {employeeDisplay && (
                        <span className="assigned-employee-badge">
                          {employeeDisplay}
                        </span>
                      )}
                      <div className="task-date-range">
                        {task.fromDate && (
                          <>
                            <span className="date-text">
                              {new Date(task.fromDate).toLocaleDateString(
                                i18n.language === "ar" ? "ar-EG" : "en-GB"
                              )}
                            </span>
                            {task.toDate && (
                              <>
                                <span className="date-separator">
                                  {t("to")}
                                </span>
                                <span className="date-text">
                                  {new Date(task.toDate).toLocaleDateString(
                                    i18n.language === "ar" ? "ar-EG" : "en-GB"
                                  )}
                                </span>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {isExpanded && tasks.length === 0 && (
          <div className="empty-state-small">
            <p>{t("No tasks in this list")}</p>
          </div>
        )}
      </div>
    );
  };

  const CompletedSection = ({ title, tasks, sectionKey }) => {
    const filtered = filterTasksByStatus(tasks);
    if (filtered.length === 0) return null;

    return (
      <div className="completed-section">
        <div
          className="completed-section-header"
          onClick={() =>
            setExpandedSections((prev) => ({
              ...prev,
              [sectionKey]: !prev[sectionKey],
            }))
          }
        >
          <h3>
            {title} <span className="task-count">({filtered.length})</span>
          </h3>
          {expandedSections[sectionKey] ? <FaChevronUp /> : <FaChevronDown />}
        </div>
        {expandedSections[sectionKey] && (
          <div className="completed-tasks-grid">
            {filtered.map((task) => (
              <TaskItem key={task.id} task={task} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Calculate filtered tasks for display
  const getDisplayTasks = () => {
    if (selectedTab === "today") return filterTasksByStatus(todayTasks);
    if (selectedTab === "future") return filterTasksByStatus(plannedTasks);
    return [];
  };

  const getTotalCompletedCount = () => {
    const filtered = [
      ...filterTasksByStatus(thisWeekCompleted),
      ...filterTasksByStatus(lastWeekCompleted),
      ...filterTasksByStatus(olderCompleted),
    ];
    return filtered.length;
  };

  return (
    <>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        itemName={selectedTaskList?.name}
      />
      <div className="tasks-page-container">
        <header className="tasks-header">
          <div className="header-title-section">
            <h1>{t("Tasks")}</h1>
            <span className="tasks-count-badge">
              {viewMode === "tasks"
                ? selectedTab === "older"
                  ? getTotalCompletedCount()
                  : getDisplayTasks().length
                : taskLists.length}{" "}
              {t("items")}
            </span>
          </div>
          <div className="header-actions">
            <button
              className="btn-primary"
              onClick={() =>
                navigate(`/fields/${userData?.Farm?.id}/tasks/new`)
              }
            >
              <FaPlus /> {t("Add Task")}
            </button>
            <button
              className="btn-secondary"
              onClick={() =>
                navigate(`/fields/${userData?.Farm?.id}/tasklists/new`)
              }
            >
              <FaList /> {t("Add Task List")}
            </button>
          </div>
        </header>

        <div className="tasks-controls">
          <div className="view-mode-toggle">
            <button
              className={`toggle-btn ${viewMode === "tasks" ? "active" : ""}`}
              onClick={() => setViewMode("tasks")}
            >
              <FaTasks /> {t("Show Tasks")}
            </button>
            <button
              className={`toggle-btn ${viewMode === "lists" ? "active" : ""}`}
              onClick={() => setViewMode("lists")}
            >
              <FaList /> {t("Show Lists")}
            </button>
          </div>

          {viewMode === "tasks" && (
            <div className="tasks-sub-controls">
              <div className="tasks-tab-navigation">
                <button
                  className={`tab-btn ${
                    selectedTab === "today" ? "active" : ""
                  }`}
                  onClick={() => setSelectedTab("today")}
                >
                  {t("Today")}
                </button>
                <button
                  className={`tab-btn ${
                    selectedTab === "future" ? "active" : ""
                  }`}
                  onClick={() => setSelectedTab("future")}
                >
                  {t("Planned")}
                </button>
                <button
                  className={`tab-btn ${
                    selectedTab === "older" ? "active" : ""
                  }`}
                  onClick={() => setSelectedTab("older")}
                >
                  {t("Older")}
                </button>
              </div>

              <div className="filter-dropdown">
                <FaFilter className="filter-icon" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">{t("All")}</option>
                  <option value="todo">{t("To Do")}</option>
                  <option value="inprogress">{t("In Progress")}</option>
                  <option value="done">{t("Done")}</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <main className="tasks-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>{t("Loading...")}</p>
            </div>
          ) : viewMode === "lists" ? (
            <div className="task-lists-grid">
              {taskLists.length > 0 ? (
                taskLists.map((listWithTasks) => (
                  <TaskListItem
                    key={listWithTasks.taskList.id}
                    taskListWithTasks={listWithTasks}
                  />
                ))
              ) : (
                <div className="empty-state">
                  <FaList className="empty-icon" />
                  <p>{t("No task lists found")}</p>
                  <button
                    className="btn-primary"
                    onClick={() =>
                      navigate(`/fields/${userData?.Farm?.id}/tasklists/new`)
                    }
                  >
                    <FaPlus /> {t("Create First Task List")}
                  </button>
                </div>
              )}
            </div>
          ) : selectedTab === "older" ? (
            <div className="completed-tasks-container">
              <CompletedSection
                title={t("This Week")}
                tasks={thisWeekCompleted}
                sectionKey="thisWeek"
              />
              <CompletedSection
                title={t("Last Week")}
                tasks={lastWeekCompleted}
                sectionKey="lastWeek"
              />
              <CompletedSection
                title={t("Older")}
                tasks={olderCompleted}
                sectionKey="older"
              />
              {getTotalCompletedCount() === 0 && (
                <div className="empty-state">
                  <FaCheckCircle className="empty-icon" />
                  <p>{t("No older tasks")}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="tasks-grid">
              {getDisplayTasks().length > 0 ? (
                getDisplayTasks().map((task) => (
                  <TaskItem key={task.id} task={task} />
                ))
              ) : (
                <div className="empty-state">
                  <FaTasks className="empty-icon" />
                  <p>
                    {selectedTab === "today"
                      ? t("No tasks for today")
                      : t("No planned tasks")}
                  </p>
                  <button
                    className="btn-primary"
                    onClick={() =>
                      navigate(`/fields/${userData?.Farm?.id}/tasks/new`)
                    }
                  >
                    <FaPlus /> {t("Create New Task")}
                  </button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default Tasks;
